import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Account } from '@ambire-common/interfaces/account'
import { getGasTankEligibility } from '@common/utils/gasTankEligibility'

import useKeystoreControllerState from '../useKeystoreControllerState'
import useNetworksControllerState from '../useNetworksControllerState'

/**
 * Extension variant — reads controller state through the background-service
 * hooks. The mobile-web / native twin lives at `@common/hooks/useHasGasTank`;
 * both delegate to the same pure `getGasTankEligibility`.
 *
 * `hasGasTank` is kept as an alias of `canUseGasTank` for the existing extension
 * call sites. It used to mean something weaker (account type only, no network
 * check), which let the UI offer a top-up whose deposit could never be spent.
 */
const useHasGasTank = ({ account }: { account: Account | null }) => {
  const { t } = useTranslation()
  const { keys } = useKeystoreControllerState()
  const { networks } = useNetworksControllerState()

  return useMemo(() => {
    const eligibility = getGasTankEligibility({ account, keys, networks, t })

    return { ...eligibility, hasGasTank: eligibility.canUseGasTank }
  }, [account, keys, networks, t])
}

export default useHasGasTank
